import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import FileUpload from './FileUpload';
import { api as attachmentService, AttachementRefType } from '../../../store/services/attachments.service';
import { generalActions } from '../../../store/actions';

/**
 * EntityFileUpload component that integrates with attachment APIs.
 * Expects attachments to be provided as props from the parent entity data.
 * Calls onAttachmentsChange callback after upload/delete to refresh parent data.
 */

const EntityFileUpload = ({
  entityId,
  entityType,
  attachments = [],
  onAttachmentsChange,
  label = "Attachments",
  ...props
}) => {
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);

  const getEntityTypeValue = (AttachementRefTypeString) => {
    return AttachementRefType[AttachementRefTypeString] ?? "";
  };

  const handleUpload = async (acceptedFiles) => {
    if (!entityId) {
      dispatch(
        generalActions.showToast({
          title: 'Entity ID is required for file upload',
          type: 'error',
        })
      );
      return;
    }

    setUploading(true);

    try {
      const entityTypeValue = getEntityTypeValue(entityType);
      if (entityTypeValue === "") {
        dispatch(
          generalActions.showToast({
            title: "Error uploading file",
            type: "error",
          })
        );
        return;
      }
      const uploadPromises = acceptedFiles.map(file =>
        attachmentService.createAttachment({
          title: file.uploadTitle || file.name,
          notes: file.uploadNotes || '',
          refId: entityId,
          refType: entityTypeValue,
          file: file,
        })
      );

      await Promise.all(uploadPromises);

      dispatch(
        generalActions.showToast({
          title: `${acceptedFiles.length} file(s) uploaded successfully`,
          type: 'success',
        })
      );

      if (onAttachmentsChange) {
        onAttachmentsChange();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      dispatch(
        generalActions.showToast({
          title: 'File upload failed. Please try again.',
          type: 'error',
        })
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileToDelete, hardDelete = false) => {
    try {
      await attachmentService.deleteAttachment(fileToDelete.id, hardDelete);

      const actionText = hardDelete ? 'deleted permanently' : 'archived';
      dispatch(
        generalActions.showToast({
          title: `File ${actionText} successfully`,
          type: 'success',
        })
      );

      if (onAttachmentsChange) {
        onAttachmentsChange();
      }
    } catch (error) {
      console.error('Delete failed:', error);
      const actionText = hardDelete ? 'delete' : 'archive';
      dispatch(
        generalActions.showToast({
          title: `Failed to ${actionText} file. Please try again.`,
          type: 'error',
        })
      );
    }
  };

  const handleEdit = async (attachmentId, updateData) => {
    try {
      await attachmentService.updateAttachment(attachmentId, updateData);

      dispatch(
        generalActions.showToast({
          title: 'File updated successfully',
          type: 'success',
        })
      );

      if (onAttachmentsChange) {
        onAttachmentsChange();
      }
    } catch (error) {
      console.error('Edit failed:', error);
      dispatch(
        generalActions.showToast({
          title: 'Failed to update file. Please try again.',
          type: 'error',
        })
      );
    }
  };

  const handleDownload = (file) => {
    if (file.url && file.url !== '#') {
      const link = document.createElement('a');
      link.href = file.url;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      dispatch(
        generalActions.showToast({
          title: 'File download URL not available',
          type: 'error',
        })
      );
    }
  };

  // Don't need to show the file upload if the entityId is not provided
  if (!entityId) {
    return null;
  }

  return (
    <FileUpload
      label={label}
      files={attachments}
      onUpload={handleUpload}
      onDelete={handleDelete}
      onEdit={handleEdit}
      onDownload={handleDownload}
      uploading={uploading}
      {...props}
    />
  );
};

export default EntityFileUpload; 